// Turns a MIDI file into a two-staff piano MusicXML score: notes are snapped to a sixteenth grid,
// split between hands, grouped into chords and voices, and cut at barlines with ties.
// Works best on MIDI exported from notation software; played-in MIDI and triplets come out rough.

import {MidiFile, midiNotes, midiTempos, midiTimeSignature} from "./midi";
import {Mode, parseScore, spellMidi, transposeScore} from "./musicxml";

// Sixteenth notes per quarter
const DIVISIONS = 4;
const MAX_VOICES = 3;
const MIDDLE_C = 60;
const NOTE_VALUES: Array<{ units: number; type: string; dot: boolean }> = [
    {units: 16, type: "whole", dot: false},
    {units: 12, type: "half", dot: true},
    {units: 8, type: "half", dot: false},
    {units: 6, type: "quarter", dot: true},
    {units: 4, type: "quarter", dot: false},
    {units: 3, type: "eighth", dot: true},
    {units: 2, type: "eighth", dot: false},
    {units: 1, type: "16th", dot: false},
];

interface Chord {
    start: number;
    end: number;
    pitches: number[];
}

interface Options {
    fifths: number;
    mode: Mode;
    title: string;
}

const escapeXml = (text: string) => text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const last = <T, >(list: T[]) => list[list.length - 1];

// Greedy split into written values, longest first
function splitLength(units: number) {
    const pieces = [];
    for (const value of NOTE_VALUES) {
        while (units >= value.units) {
            pieces.push(value);
            units -= value.units;
        }
    }
    return pieces;
}

// Two tracks (or channels) with notes are taken as the two hands; otherwise hands split at middle C
function staffOf(notes: ReturnType<typeof midiNotes>): (note: ReturnType<typeof midiNotes>[number]) => 1 | 2 {
    const partKey = (n: { track: number; channel: number }) => n.track * 16 + n.channel;
    const parts = new Map<number, number[]>();
    notes.forEach(n => parts.set(partKey(n), [...(parts.get(partKey(n)) ?? []), n.pitch]));
    if (parts.size === 2) {
        const [[a, pa], [b, pb]] = Array.from(parts);
        const mean = (list: number[]) => list.reduce((s, x) => s + x, 0) / list.length;
        const upper = mean(pa) >= mean(pb) ? a : b;
        return n => (partKey(n) === upper ? 1 : 2);
    }
    return n => (n.pitch >= MIDDLE_C ? 1 : 2);
}

function toVoices(chords: Chord[]): Chord[][] {
    const voices: Chord[][] = [];
    for (const chord of chords) {
        let voice = voices.find(v => last(v).end <= chord.start);
        // A sixteenth of legato overlap is trimmed rather than opening a new voice
        voice ??= voices.find(v => last(v).end - chord.start <= 1 && last(v).start < chord.start);
        if (voice && last(voice).end > chord.start) last(voice).end = chord.start;
        if (!voice && voices.length < MAX_VOICES) voices.push((voice = []));
        if (!voice) {
            voice = voices.reduce((a, b) => (last(a).end <= last(b).end ? a : b));
            const previous = last(voice);
            if (previous.start >= chord.start) {
                previous.pitches.push(...chord.pitches);
                previous.end = Math.min(previous.end, chord.end);
                continue;
            }
            previous.end = chord.start;
        }
        voice.push(chord);
    }
    return voices;
}

function staffChords(notes: Array<{ start: number; end: number; pitch: number }>): Chord[] {
    // Same pitch must not overlap itself after snapping
    const byPitch = [...notes].sort((a, b) => a.pitch - b.pitch || a.start - b.start);
    const kept: typeof notes = [];
    for (const n of byPitch) {
        const prev = last(kept);
        if (prev && prev.pitch === n.pitch && prev.end > n.start) {
            if (prev.start === n.start) {
                prev.end = Math.max(prev.end, n.end);
                continue;
            }
            prev.end = n.start;
        }
        kept.push({...n});
    }
    const chords = new Map<string, Chord>();
    for (const n of kept) {
        const key = `${n.start}:${n.end}`;
        const chord = chords.get(key) ?? {start: n.start, end: n.end, pitches: []};
        chord.pitches.push(n.pitch);
        chords.set(key, chord);
    }
    return Array.from(chords.values())
        .map(c => ({...c, pitches: c.pitches.sort((a, b) => b - a)}))
        .sort((a, b) => a.start - b.start || b.pitches[0] - a.pitches[0]);
}

export function midiToMusicXml(midi: MidiFile, {fifths, mode, title}: Options): Document {
    const grid = midi.ppq / DIVISIONS;
    const snap = (tick: number) => Math.round(tick / grid);
    const time = midiTimeSignature(midi);
    const measureLength = Math.max(1, Math.round((time.beats * DIVISIONS * 4) / time.beatType));

    const notes = midiNotes(midi);
    const staff = staffOf(notes);
    const staves: Chord[][][] = ([1, 2] as const).map(s => toVoices(staffChords(
        notes.filter(n => staff(n) === s).map(n => {
            const start = snap(n.start);
            return {start, end: Math.max(start + 1, snap(n.end)), pitch: n.pitch};
        }))));
    const end = Math.max(1, ...staves.flat(2).map(c => c.end));
    const measureCount = Math.ceil(end / measureLength);

    const tempos = midiTempos(midi).map(t => ({
        measure: Math.floor((t.at * DIVISIONS) / measureLength), bpm: Math.round(t.bpm),
    }));

    const noteXml = (pitch: number | null, units: number, type: string | null, dot: boolean, voice: number,
                     staffNumber: number, chord: boolean, tieStop: boolean, tieStart: boolean) => {
        const spelled = pitch === null ? null : spellMidi(pitch, fifths);
        const pitchXml = spelled
            ? `<pitch><step>${spelled.step}</step>${spelled.alter ? `<alter>${spelled.alter}</alter>` : ""}<octave>${spelled.octave}</octave></pitch>`
            : type ? "<rest/>" : '<rest measure="yes"/>';
        const ties = (tieStop ? '<tie type="stop"/>' : "") + (tieStart ? '<tie type="start"/>' : "");
        const tied = (tieStop ? '<tied type="stop"/>' : "") + (tieStart ? '<tied type="start"/>' : "");
        return `<note>${chord ? "<chord/>" : ""}${pitchXml}<duration>${units}</duration>${ties}`
            + `<voice>${voice}</voice>${type ? `<type>${type}</type>` : ""}${dot ? "<dot/>" : ""}`
            + `<staff>${staffNumber}</staff>${tied ? `<notations>${tied}</notations>` : ""}</note>`;
    };

    // One voice inside one measure; returns the XML and how far it moved the measure cursor
    const voiceXml = (voice: Chord[], from: number, to: number, voiceNumber: number, staffNumber: number,
                      filled: boolean): [string, number] => {
        let xml = "";
        let cursor = from;
        const gap = (length: number) => {
            if (!filled) {
                xml += `<forward><duration>${length}</duration><voice>${voiceNumber}</voice><staff>${staffNumber}</staff></forward>`;
            } else if (length === to - from) {
                xml += noteXml(null, length, null, false, voiceNumber, staffNumber, false, false, false);
            } else {
                splitLength(length).forEach(v =>
                    (xml += noteXml(null, v.units, v.type, v.dot, voiceNumber, staffNumber, false, false, false)));
            }
        };
        for (const chord of voice) {
            if (chord.end <= from || chord.start >= to) continue;
            const start = Math.max(chord.start, from);
            const stop = Math.min(chord.end, to);
            if (start > cursor) gap(start - cursor);
            const pieces = splitLength(stop - start);
            for (let i = 0; i < pieces.length; i++) {
                const piece = pieces[i];
                const tieStop = i > 0 || start > chord.start;
                const tieStart = i < pieces.length - 1 || stop < chord.end;
                for (let j = 0; j < chord.pitches.length; j++) {
                    xml += noteXml(chord.pitches[j], piece.units, piece.type, piece.dot, voiceNumber, staffNumber,
                        j > 0, tieStop, tieStart);
                }
            }
            cursor = stop;
        }
        if (filled && cursor < to) {
            gap(to - cursor);
            cursor = to;
        }
        return [xml, cursor - from];
    };

    let measures = "";
    for (let m = 0; m < measureCount; m++) {
        const from = m * measureLength;
        const to = from + measureLength;
        let body = "";
        if (m === 0) {
            body += `<attributes><divisions>${DIVISIONS}</divisions>`
                + `<key><fifths>${fifths}</fifths><mode>${mode}</mode></key>`
                + `<time><beats>${time.beats}</beats><beat-type>${time.beatType}</beat-type></time>`
                + "<staves>2</staves>"
                + '<clef number="1"><sign>G</sign><line>2</line></clef>'
                + '<clef number="2"><sign>F</sign><line>4</line></clef></attributes>';
        }
        const tempo = tempos.filter(t => t.measure === m).pop();
        if (tempo) {
            const mark = m === 0
                ? `<direction-type><metronome><beat-unit>quarter</beat-unit><per-minute>${tempo.bpm}</per-minute></metronome></direction-type>`
                : "";
            body += `<direction placement="above">${mark}<staff>1</staff><sound tempo="${tempo.bpm}"/></direction>`;
        }

        const parts: Array<[string, number]> = [];
        staves.forEach((voices, s) => {
            const list = voices.length ? voices : [[]];
            list.forEach((voice, v) => {
                const voiceNumber = s * 4 + v + 1;
                // The first voice of each staff always fills the bar; extra voices only where they play
                const [xml, moved] = voiceXml(voice, from, to, voiceNumber, s + 1, v === 0);
                if (xml) parts.push([xml, moved]);
            });
        });
        parts.forEach(([xml, moved], i) => {
            body += xml;
            if (i < parts.length - 1 && moved > 0) body += `<backup><duration>${moved}</duration></backup>`;
        });
        measures += `<measure number="${m + 1}">${body}</measure>`;
    }

    const xml = '<?xml version="1.0" encoding="UTF-8"?>'
        + '<score-partwise version="3.1">'
        + `<work><work-title>${escapeXml(title)}</work-title></work>`
        + '<part-list><score-part id="P1"><part-name print-object="no">Piano</part-name></score-part></part-list>'
        + `<part id="P1">${measures}</part></score-partwise>`;
    // A transposition by nothing writes the accidentals the key signature needs
    return transposeScore(parseScore(xml), 0, 0);
}
